<script lang="ts">
  import { createEventDispatcher, onMount, SvelteComponent } from "svelte";
  import * as _ from "lodash";
  import Icon from "../Icon.svelte";
  import type { Writable } from "svelte/store";
  import type { Field } from "svelte-forms2/types";

  type FormField = Writable<Field<any>>;

  export let apiHandler: (input: any) => Promise<{ data: Promise<any> }> = undefined;
  export let apiEndpoint: string = "";
  export let value = null; //initial data
  export let text = ""; //initial display data for non list mode
  export let placeholder;
  export let disabled = false;
  export let dropdownItemTemplateCB = null;
  export let dropdownComponent: typeof SvelteComponent = null;
  export let showInputWhenDisabled = false;
  export let onChange = () => {};
  export let inputId = null;
  export let minInputCharLengthForApiRequest = 3;
  export let addFromAutocompleteOnly = null;
  export let autocapitalize = "off";
  export let autocorrect = "off";
  export let autocomplete = "off";
  export let autofocus = false;
  export let showCloseIcon = true;
  export let field: FormField = undefined;
  export let popoverFullWidth = false;
  export let clickedIntoInput: boolean = null;
  export let leftInput: boolean = null;
  export let showResultsWithoutFocus: boolean = false;

  let lastValueText = "";
  $: submitting = false;

  $: {
    let newValueText;
    if (value && value.name) {
      newValueText = value.name;
    } else {
      newValueText = "";
    }

    if (newValueText != lastValueText) {
      text = newValueText;
      lastValueText = newValueText;
    }
  }

  onMount(() => {
    if (autofocus && input) {
      input.focus();
    }
  });

  if (!apiHandler && apiEndpoint) {
    apiHandler = async input => {
      return await fetch(`${apiEndpoint}search=${input}`).then(res => {
        return { data: res.json() };
      });
    };
  }

  const dispatch = createEventDispatcher();

  let opened = false;
  let data = [];
  let highlightIndex = 0;
  let input, list;
  let filteredData: { name: string; highlighted: { name: string; id: any } }[];

  $: {
    const hlfilter = highlightFilter(text, ["name"]);
    filteredData = !text ? data : data.filter(item => item).map(hlfilter);
  }

  function selectItem() {
    value = filteredData[highlightIndex];
    onclick(value);
  }

  function up() {
    open();
    if (highlightIndex > 0) highlightIndex--;
    highlight();
  }

  function down() {
    open();
    if (highlightIndex < filteredData.length - 1) highlightIndex++;
    highlight();
  }

  function highlight() {
    const el = list.querySelector(".selected");
    if (el && el.scrollIntoViewIfNeeded) el.scrollIntoViewIfNeeded();
  }

  function onclick(item) {
    if (!addFromAutocompleteOnly && !item) {
      item = text;
    }
    value = item;
    if (value) {
      text = value.name;
    }
    dispatch("change", item);
    dispatch("select", item);
    close();
  }

  function onkeydown(e) {
    let key = e.key;
    if (key === "Tab" && e.shiftKey) key = "ShiftTab";
    const fnmap = {
      Tab: opened ? down.bind(this) : null,
      ShiftTab: opened ? up.bind(this) : null,
      ArrowDown: down.bind(this),
      ArrowUp: up.bind(this),
      Escape: onEsc.bind(this),
      Backspace: value && onEsc.bind(this),
    };
    const fn = fnmap[key];
    if (typeof fn === "function") {
      e.preventDefault();
      fn(e);
    }
  }

  let focused = false;

  function onfocus(e) {
    focused = true;
  }

  function oninput(e) {
    text = e.target.value;
    if (text && text.length >= minInputCharLengthForApiRequest) {
      open();

      highlightIndex = 0;
    } else if (text == "" || text == null) {
      value = null;
      close();
    }
  }

  function onkeypress(e) {
    if (e.key === "Enter") {
      if (!addFromAutocompleteOnly || opened) {
        e.preventDefault();
        selectItem();
      }
    }
  }

  function onEsc() {
    if (text) return clear();
  }

  function clear() {
    text = "";
    dispatch("clear");
    close();
    setTimeout(
      () =>
        function () {
          if (input) {
            input.focus();
          }
        },
    );
  }

  async function _open() {
    if (!focused) {
      return;
    }
    submitting = true;
    let results = await apiHandler(text);
    data = await results.data;
    submitting = false;
    if (!showResultsWithoutFocus && !focused) {
      return;
    }
    opened = true;
  }

  let open = _.debounce(_open, 400);

  export const show = value => {
    focused = true;
    text = value;
    input.focus();
    _open();
  };

  function onblur() {
    dispatch("blur", text);
    focused = false;
    close();
  }

  export let closeAfterSelect = function () {
    opened = false;
    text = "";
  };

  const close = function () {
    opened = false;

    // If no selection made, clear the text
    if (addFromAutocompleteOnly && !value) {
      closeAfterSelect();
    }
  };

  // 'item number one'.replace(/(it)(.*)(nu)(.*)(one)/ig, '<b>$1</b>$2 <b>$3</b>$4 <b>$5</b>')
  function highlightFilter(q, fields) {
    q = q
      ?.replace("\\", "")
      ?.replace("[", "")
      ?.replace("]", "")
      ?.replace("(", "\\(")
      ?.replace(")", "\\)")
      ?.replace("+", "")
      ?.replaceAll("*", "\\*");
    const qs = "(" + q?.trim()?.replace(/\s/g, ")(.*)(") + ")";
    const reg = new RegExp(qs, "ig");
    let n = 1,
      len = qs.split(")(").length + 1,
      repl = "";
    for (; n < len; n++) repl += n % 2 ? `<span class="default-body--bold">$${n}</span>` : `$${n}`;

    return i => {
      const newI = Object.assign({ highlighted: {} }, i);
      if (fields) {
        fields.forEach(f => {
          if (!newI[f]) return;
          newI.highlighted[f] = newI[f].replace(reg, repl);
        });
      }
      return newI;
    };
  }

  function preventBlur(e) {
    e.preventDefault();
  }

  function handleClickedIntoInput() {
    clickedIntoInput = true;
  }

  function handleLeftInput() {
    leftInput = true;
  }
</script>

<style>
  .autocomplete-input:disabled {
    background: #eaeaea;
    cursor: not-allowed;
  }
  .icon {
    margin-left: -10px;
  }
  .popover-full-width {
    width: 100% !important;
  }
</style>

{#if disabled && !showInputWhenDisabled}
  <div class="default-body--regular">
    {text}
  </div>
{:else}
  <div class="fizz-styles fizz-flex-row">
    <input
      type="text"
      class="autocomplete-input fizz-input-text"
      style="width: 100%"
      id={inputId}
      data-testid={inputId}
      {disabled}
      {autocapitalize}
      {autocorrect}
      {autocomplete}
      bind:this={input}
      bind:value={text}
      on:input={oninput}
      on:focus={onfocus}
      on:keydown={onkeydown}
      on:keypress={onkeypress}
      {placeholder}
      on:blur={onblur}
      on:change={onChange}
      on:click={handleClickedIntoInput}
      on:mouseleave={handleLeftInput}
      class:fizz-input-error={field && !$field?.valid}
    />
    {#if showCloseIcon}
      <button
        class="fizz-icon-button-small icon"
        on:click={e => {
          clear();
          e.preventDefault();
        }}
      >
        <Icon type="Close" />
      </button>
    {/if}
  </div>
{/if}
{#if submitting}
  <div
    class="fizz-flex-row-8 fizz-align-content-start fizz-highlight-on-hover fizz-inset-squish-16 fizz-icon-spinner-small"
  />
{:else}
  <div class="autocomplete-list-parent">
    <div
      class="autocomplete-list margin-top--smaller {opened ? '' : 'hidden'}"
      class:popover-full-width={popoverFullWidth}
      bind:this={list}
      on:mousedown={preventBlur}
    >
      {#each filteredData as item, i}
        <div
          class="autocomplete-list-item {i === highlightIndex ? 'selected' : ''}"
          on:click={() => onclick(item)}
          on:mouseenter={() => (highlightIndex = i)}
        >
          {#if dropdownItemTemplateCB}
            {@html dropdownItemTemplateCB(item)}
          {:else if dropdownComponent}
            <svelte:component this={dropdownComponent} {item} />
          {:else}
            {@html (item.highlighted && item.highlighted.name) || item.name}
          {/if}
        </div>
      {/each}

      {#if (text || "") !== "" && (filteredData || []).length == 0}
        {#if $$slots.no_results}
          <slot name="no_results" {text} />
        {:else}
          <div class="autocomplete-list-item">
            <span class="default-body--regular">Nothing matches your search</span>
          </div>
        {/if}
        <slot />
      {/if}
    </div>
  </div>
{/if}
